import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Bell, BellOff, Plus, X, Utensils, Bike, Trash2, ChevronRight, Clock, Navigation } from "lucide-react";
import { MenuPicker } from "../../components/MenuPicker";

const COLUMNS = [
  { key: "pending", title: "Gelen Siparişler", next: "preparing", nextLabel: "Hazırla", accent: "border-amber-500/40" },
  { key: "preparing", title: "Hazırlanıyor", next: "ready", nextLabel: "Hazır", accent: "border-blue-500/40" },
  { key: "ready", title: "Hazır / Serviste", next: "completed", nextLabel: "Tamamla", accent: "border-emerald-500/40" },
  { key: "completed", title: "Tamamlanan", next: null, nextLabel: "", accent: "border-zinc-600/40" },
];

const ITEM_ST = {
  pending: { label: "Bekliyor", cls: "text-amber-400 border-amber-500/30 bg-amber-950/30" },
  preparing: { label: "Hazırlanıyor", cls: "text-blue-400 border-blue-500/30 bg-blue-950/30" },
  delivered: { label: "Teslim", cls: "text-emerald-400 border-emerald-500/30 bg-emerald-950/30" },
};

const timeStr = (iso) => new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });

const TABLE_GROUPS = [
  { group: "İç Mekan", items: Array.from({ length: 10 }, (_, i) => `İç ${i + 1}`) },
  { group: "Bahçe", items: Array.from({ length: 7 }, (_, i) => `Bahçe ${i + 1}`) },
];

const tableStage = (s) => {
  const unpaid = (s.items || []).filter((i) => !i.paid);
  if (!unpaid.length) return null;
  if (unpaid.some((i) => i.status === "pending")) return "pending";
  if (unpaid.some((i) => i.status === "preparing")) return "preparing";
  return "ready";
};

export default function OrderBoard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sound, setSound] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const prevCount = useRef(null);

  const load = async () => {
    try {
      const [o, s] = await Promise.all([api.get("/orders"), api.get("/tables/sessions")]);
      const pendingNow =
        o.data.filter((x) => x.status === "pending").length +
        s.data.reduce((n, sess) => n + (sess.items || []).filter((i) => !i.paid && i.status === "pending").length, 0);
      if (prevCount.current !== null && sound && pendingNow > prevCount.current) beep();
      prevCount.current = pendingNow;
      setOrders(o.data);
      setSessions(s.data);
    } catch {}
  };

  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [sound]);

  const beep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination); o.frequency.value = 880; o.type = "sine";
      g.gain.setValueAtTime(0.2, ctx.currentTime); o.start();
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4); o.stop(ctx.currentTime + 0.4);
    } catch {}
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/orders/${id}/status`, { status }); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const advance = async (name, from) => {
    try { await api.post(`/tables/${encodeURIComponent(name)}/advance`, { fromStatus: from }); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const clearHistory = async () => {
    if (!window.confirm("Tamamlanan ve iptal edilen siparişler silinsin mi?")) return;
    try { await api.delete("/orders/clear-history"); toast.success("Geçmiş temizlendi"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const grouped = useMemo(() => {
    const g = { pending: [], preparing: [], ready: [], completed: [] };
    orders.forEach((o) => { if (o.status === "cancelled") return; (g[o.status] || (g[o.status] = [])).push(o); });
    return g;
  }, [orders]);

  const tablesByCol = useMemo(() => {
    const m = { pending: [], preparing: [], ready: [], completed: [] };
    sessions.forEach((s) => { const st = tableStage(s); if (st) m[st].push(s); });
    return m;
  }, [sessions]);

  const colCount = (key) => (grouped[key]?.length || 0) + (tablesByCol[key]?.length || 0);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl text-white">Canlı Sipariş Panosu</h1>
          <p className="text-zinc-400 text-sm mt-1">Masa ve paket siparişleri anlık takip edin — masa siparişleri Adisyon ile senkron</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setSound(!sound)} className="btn-outline px-3 py-2.5 rounded-xl" title="Sesli bildirim" data-testid="sound-toggle">
            {sound ? <Bell size={18} /> : <BellOff size={18} />}
          </button>
          {user?.role === "owner" && <button onClick={clearHistory} className="btn-outline px-3 py-2.5 rounded-xl" title="Geçmişi Temizle" data-testid="clear-history-btn"><Trash2 size={18} /></button>}
          <button onClick={() => setShowNew(true)} className="btn-gold px-5 py-2.5 rounded-xl flex items-center gap-2" data-testid="new-order-btn"><Plus size={18} /> Yeni Sipariş</button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        {COLUMNS.map((col) => (
          <div key={col.key} className="flex flex-col" data-testid={`col-${col.key}`}>
            <div className={`flex items-center justify-between px-4 py-3 rounded-t-xl bg-[#121215] border-t-2 ${col.accent} border-x border-[#D4AF37]/15`}>
              <span className="font-serif text-lg text-white">{col.title}</span>
              <span className="font-mono text-sm text-[#FFE599] bg-[#0B0B0C] px-2.5 py-0.5 rounded-full">{colCount(col.key)}</span>
            </div>
            <div className="flex-1 space-y-3 p-3 bg-[#0E0E10] border-x border-b border-[#D4AF37]/15 rounded-b-xl min-h-[200px]">
              {(tablesByCol[col.key] || []).map((s) => {
                const unpaid = (s.items || []).filter((i) => !i.paid);
                const unpaidTotal = unpaid.reduce((sum, i) => sum + i.price * i.qty, 0);
                return (
                  <div key={s.id} className="card-lux p-4 scale-in border-[#D4AF37]/40 bg-[#D4AF37]/5" data-testid={`tablecard-${s.tableName}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="flex items-center gap-1.5 font-mono text-xs px-2 py-1 rounded-lg bg-[#D4AF37]/20 text-[#FFE599]"><Utensils size={12} />{s.tableName}</span>
                      <span className="text-[10px] font-mono uppercase tracking-wide text-[#D4AF37]">Masa • Adisyon</span>
                    </div>
                    <div className="space-y-1 mb-3">
                      {unpaid.map((i) => (
                        <div key={i.lineId} className="text-sm text-zinc-200 flex justify-between items-center gap-2">
                          <span>{i.qty}x {i.name}</span>
                          <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-full border shrink-0 ${ITEM_ST[i.status]?.cls || ""}`}>{ITEM_ST[i.status]?.label || i.status}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-[#D4AF37]/15">
                      <span className="font-mono text-[#FFE599]">₺{unpaidTotal.toFixed(2)}</span>
                      {col.key === "pending" && <button onClick={() => advance(s.tableName, "pending")} className="btn-gold px-3 py-1.5 rounded-lg text-xs flex items-center gap-1" data-testid={`tadvance-${s.tableName}`}>Hazırla<ChevronRight size={14} /></button>}
                      {col.key === "preparing" && <button onClick={() => advance(s.tableName, "preparing")} className="btn-gold px-3 py-1.5 rounded-lg text-xs flex items-center gap-1" data-testid={`tadvance-${s.tableName}`}>Teslim Et<ChevronRight size={14} /></button>}
                      {col.key === "ready" && <span className="text-[10px] text-amber-400 font-mono">Masalar'dan ödeme al</span>}
                    </div>
                  </div>
                );
              })}

              {(grouped[col.key] || []).map((o) => (
                <div key={o.id} className="card-lux p-4 scale-in" data-testid={`order-${o.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="flex items-center gap-1.5 font-mono text-xs px-2 py-1 rounded-lg bg-[#D4AF37]/10 text-[#FFE599]">
                      {o.type === "table" ? <><Utensils size={12} />{o.tableNo}</> : <><Bike size={12} />Paket</>}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-zinc-500 font-mono"><Clock size={11} />{timeStr(o.createdAt)}</span>
                  </div>
                  <div className="text-xs text-zinc-400 mb-2">{o.customerName} • {o.customerPhone}</div>
                  <div className="space-y-1 mb-3">
                    {o.items.map((it, i) => <div key={i} className="text-sm text-zinc-200 flex justify-between"><span>{it.qty}x {it.name}</span></div>)}
                  </div>
                  {o.note && <div className="text-xs text-amber-300/90 mb-2 italic">Not: {o.note}</div>}
                  {o.type === "delivery" && o.address && (
                    <div className="mb-2">
                      <div className="text-[11px] text-zinc-400 mb-1">📍 {o.address}</div>
                      <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(o.address)}`} target="_blank" rel="noopener noreferrer" className="btn-outline inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-lg" data-testid={`route-${o.id}`}>
                        <Navigation size={12} /> Yol Tarifi
                      </a>
                    </div>
                  )}
                  <div className="text-[11px] text-emerald-300/90 mb-2">Ödeme (kapıda): {o.paymentMethod === "card" ? "Kart / POS" : "Nakit"}</div>
                  <div className="flex items-center justify-between pt-2 border-t border-[#D4AF37]/15">
                    <span className="font-mono text-[#FFE599]">₺{o.total.toFixed(2)}</span>
                    <div className="flex gap-1.5">
                      {o.status !== "completed" && <button onClick={() => setStatus(o.id, "cancelled")} className="text-rose-400 hover:bg-rose-500/10 h-8 w-8 rounded-lg flex items-center justify-center" data-testid={`cancel-${o.id}`}><X size={16} /></button>}
                      {col.next && <button onClick={() => setStatus(o.id, col.next)} className="btn-gold px-3 py-1.5 rounded-lg text-xs flex items-center gap-1" data-testid={`advance-${o.id}`}>{col.nextLabel}<ChevronRight size={14} /></button>}
                    </div>
                  </div>
                </div>
              ))}
              {colCount(col.key) === 0 && <p className="text-center text-zinc-600 text-sm py-8">—</p>}
            </div>
          </div>
        ))}
      </div>

      {showNew && <NewOrderModal onClose={() => setShowNew(false)} onDone={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewOrderModal({ onClose, onDone }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState({});
  const [tableNo, setTableNo] = useState("İç 1");
  const [person, setPerson] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.isAvailable)));
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const add = (p) => setCart((c) => ({ ...c, [p.id]: { p, qty: (c[p.id]?.qty || 0) + 1 } }));
  const sub = (p) => setCart((c) => { const q = (c[p.id]?.qty || 0) - 1; const n = { ...c }; if (q <= 0) delete n[p.id]; else n[p.id] = { p, qty: q }; return n; });
  const items = Object.values(cart);
  const total = items.reduce((s, i) => s + i.p.price * i.qty, 0);

  const submit = async () => {
    if (items.length === 0) return toast.error("Ürün seçin");
    setBusy(true);
    try {
      await api.post(`/tables/${encodeURIComponent(tableNo)}/items`, { items: items.map((i) => ({ productId: i.p.id, name: i.p.name, price: i.p.price, qty: i.qty, note: "", person: person.trim() })) });
      toast.success(`${tableNo} adisyonuna eklendi`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="new-order-modal">
      <div className="card-lux w-full max-w-2xl max-h-[88vh] flex flex-col scale-in">
        <div className="px-5 py-4 border-b border-[#D4AF37]/20 flex items-center justify-between">
          <h2 className="font-serif text-2xl gold-text">Yeni Sipariş (Masa)</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="new-order-close"><X size={24} /></button>
        </div>
        <div className="px-5 pt-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-zinc-400">Masa</label>
            <select value={tableNo} onChange={(e) => setTableNo(e.target.value)} className="lux-input w-40" data-testid="new-order-table">
              {TABLE_GROUPS.map((g) => (
                <optgroup key={g.group} label={g.group}>
                  {g.items.map((t) => <option key={t} value={t}>{t}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 flex-1 min-w-[160px]">
            <label className="text-sm text-zinc-400 whitespace-nowrap">Kişi</label>
            <input value={person} onChange={(e) => setPerson(e.target.value)} placeholder="Opsiyonel (ör. Ahmet)" className="lux-input py-2 text-sm" data-testid="new-order-person" />
          </div>
        </div>
        <p className="px-5 pt-2 text-[11px] text-zinc-500">{tableNo} adisyonuna eklenir{person.trim() ? ` — ${person.trim()} adına` : ""}. Masalar ekranında görünür.</p>
        <div className="flex-1 overflow-y-auto px-5 py-3 no-scrollbar">
          <MenuPicker products={products} categories={categories} cart={cart} onAdd={add} onSub={sub} maxListHeight="max-h-[46vh]" />
        </div>
        <div className="p-5 border-t border-[#D4AF37]/20 flex items-center justify-between">
          <span className="font-mono text-lg text-[#FFE599]">₺{total.toFixed(2)}</span>
          <button onClick={submit} disabled={busy} className="btn-gold px-8 py-3 rounded-xl" data-testid="new-order-submit">{busy ? "..." : "Adisyona Ekle"}</button>
        </div>
      </div>
    </div>
  );
}
